import { useEffect, useState } from 'react'
import { api } from '../Services/api'
import { track } from '../Utils/analytics'
import { KEYS, readJSON, writeJSON } from '../Utils/storage'
import { norm } from '../Utils/text'

export function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

const results = new Map() // normalized query -> response, for this visit

/* Topics, categories, sources and the first stories for a query, while typing. */
export function useSearch(query, delay = 200) {
  const q = useDebounced(query.trim(), delay)
  const key = norm(q)
  const [state, setState] = useState({ key: '', data: null, loading: false, error: null })

  useEffect(() => {
    if (key.length < 2) {
      setState({ key, data: null, loading: false, error: null })
      return undefined
    }
    if (results.has(key)) {
      setState({ key, data: results.get(key), loading: false, error: null })
      return undefined
    }
    let alive = true
    setState((s) => ({ ...s, key, loading: true, error: null }))
    api
      .search(q)
      .then((data) => {
        results.set(key, data)
        track('search_query', { length: q.length })
        if (alive) setState({ key, data, loading: false, error: null })
      })
      .catch((error) => alive && setState({ key, data: null, loading: false, error }))
    return () => {
      alive = false
    }
  }, [key, q])

  const fresh = state.key === key
  return {
    query: q,
    data: fresh ? state.data : null,
    loading: key.length >= 2 && (!fresh || state.loading),
    error: fresh ? state.error : null,
  }
}

/* Mobile Istraži: the last searches, newest first (spec 7.3). */
const MAX_RECENT = 8

export function readRecentSearches() {
  const list = readJSON(KEYS.recentSearches, [])
  return Array.isArray(list) ? list.filter((s) => typeof s === 'string') : []
}

export function saveRecentSearch(query) {
  const text = String(query || '')
    .replace(/\s+/g, ' ')
    .trim()
  if (text.length < 2) return readRecentSearches()
  const next = [text, ...readRecentSearches().filter((s) => norm(s) !== norm(text))].slice(
    0,
    MAX_RECENT
  )
  writeJSON(KEYS.recentSearches, next)
  return next
}

export function removeRecentSearch(query) {
  const next = readRecentSearches().filter((s) => s !== query)
  writeJSON(KEYS.recentSearches, next)
  return next
}

export function clearRecentSearches() {
  writeJSON(KEYS.recentSearches, [])
  return []
}
